/* eslint-disable max-len */
import { AddPaymentMethodModal } from 'common/add-payment-method-modal';
import { ButtonStyle } from 'common/button';
import { PaymentType, SubModificationType } from 'common/enums';
import { ModalWarning } from 'common/modal-warning';
import { appActions, AppContext } from 'context';
import { trackCustomEvent } from 'helpers/analytics';
import { dayjs } from 'helpers/dayjs';
import { SubscriptionType } from 'models/subscription-type';
import { SubscriptionController } from 'networking/controllers/subscripton-controller';
import { UserController } from 'networking/controllers/user-controller';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { goToPage, RouteName } from 'routes';
import styles from './all-payment-process.module.scss';
import { ModalProcessingVaultPayment } from './components/modal-processing-payment';
import { ModifySubscriptionModal } from './components/modify-subscription-modal';
import { UpgradeConfirmSubscriptionModal } from './components/upgrade-confirm-subscription-modal';

type AllPaymentProcessProps = {
  paymentType: PaymentType;
  title?: string;
  vaultReceived?: VaultType;
  confirmPaymentTextBody?: ReactNode | string;
  payButtonText?: string;
  successPaymentTextBody: string;
  successPaymentButtonText?: string;
  failedPaymentTextBody: string;
  creatingFreeTrial?: boolean;
  payVaultWithFullDiscout?: boolean;
  isInsideVideoCall?: boolean;
  notifyProcessConfirmation: (
    tier?: SubscriptionSchemeId,
  ) => Promise<VaultType | null>;
  cancelPaymentProcess: () => void;
  successPaymentProcess: (newTier: SubscriptionSchemeId) => void;
  failedPaymentProcess: () => void;
  notifyError: () => void;
  dataCy?: string;
};

const AllPaymentProcess: React.FC<AllPaymentProcessProps> = ({
  vaultReceived = null,
  paymentType,
  title = '',
  confirmPaymentTextBody = '',
  payButtonText = '',
  successPaymentTextBody,
  successPaymentButtonText = 'Okay',
  failedPaymentTextBody,
  creatingFreeTrial = false,
  payVaultWithFullDiscout = false,
  isInsideVideoCall = false,
  notifyProcessConfirmation,
  cancelPaymentProcess,
  successPaymentProcess,
  failedPaymentProcess,
  notifyError,
  dataCy,
}) => {
  const { state, dispatch } = useContext(AppContext);

  const [confirmCreateVaultProcess, setConfirmCreateVaultProcess] =
    useState(false);
  const [addPaymentMethodModal, setAddPaymentMethodModal] = useState(false);

  const [processingVaultPaymentModal, setProcessingVaultPaymentModal] =
    useState(false);
  const [showPaymentSuccesfulModal, setShowPaymentSuccesfulModal] =
    useState(false);
  const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(false);

  const [discountedPrices, setDiscountedPrices] = useState<
    SubscriptionDiscountedPriceType[]
  >([]);

  const [
    showModifyVaultSubscriptionModal,
    setShowModifyVaultSubscriptionModal,
  ] = useState(false);
  const [
    showDowngradeConfirmSubscriptionModal,
    setShowDowngradeConfirmSubscriptionModal,
  ] = useState(false);

  const [
    showUpgradeConfirmSubscriptionModal,
    setShowUpgradeConfirmSubscriptionModal,
  ] = useState(false);

  const [vault, setVault] = useState<VaultType | null>(vaultReceived);

  const [loading, setLoading] = useState<boolean>(false);

  const subscriptionSeleted = useRef<SubscriptionTypeType>(
    new SubscriptionType(),
  );
  const newTier = useRef<SubscriptionSchemeId | null>(null);

  const { data, subscriptionsScheme } = state;

  const getSubscriptionPrices = async () => {
    const response = await SubscriptionController.getSubscriptionPrices();
    setDiscountedPrices(response.prices);
  };

  const startProcess = () => {
    // Create free vault OR create vault with full discount (no payment method needed)
    if (creatingFreeTrial || payVaultWithFullDiscout) {
      setConfirmCreateVaultProcess(true);
      return;
    }

    // Not payment method associated
    if (!data.user.paymentMethods?.length) {
      trackCustomEvent('startAddPaymentMethod', { step: 'createVault' });
      setAddPaymentMethodModal(true);
      return;
    }

    // Create paid vault
    if (paymentType === PaymentType.CreateNewVault) {
      setConfirmCreateVaultProcess(true);
      return;
    }

    // Modify vault subscription
    if (paymentType === PaymentType.ModifyVaultSubscription) {
      setShowModifyVaultSubscriptionModal(true);
    }
  };

  useEffect(() => {
    getSubscriptionPrices();
    startProcess();
  }, []);

  const handleUupdateUserData = async () => {
    const dataResponse = await UserController.me();
    dispatch({ type: appActions.USER_LOGGED, data: dataResponse });

    getSubscriptionPrices();
  };

  const handleConfirmOperation = async (
    isFreeTrial: boolean,
    subModificationType?: SubModificationType,
    newTierSelected?: SubscriptionSchemeId,
  ) => {
    trackCustomEvent('confirmVaultPayment');
    setLoading(true);
    const vaultResponse = await notifyProcessConfirmation(newTierSelected);
    setLoading(false);

    if (subModificationType === SubModificationType.downgrade) {
      setShowDowngradeConfirmSubscriptionModal(false);
    } else if (subModificationType === SubModificationType.upgrade) {
      setShowUpgradeConfirmSubscriptionModal(false);
    } else {
      setConfirmCreateVaultProcess(false);
    }

    if (vaultResponse) {
      setVault(vaultResponse);

      if (
        isFreeTrial ||
        payVaultWithFullDiscout ||
        subModificationType === SubModificationType.downgrade
      ) {
        const allData = await UserController.me();
        dispatch({ type: appActions.USER_LOGGED, data: allData });

        successPaymentProcess(vaultResponse.subscriptionType);
      } else {
        setProcessingVaultPaymentModal(true);
      }
    } else {
      notifyError();
    }
  };

  const showCreateFreeTrialVaultModalComponent = () => (
    <ModalWarning
      title={title}
      content="This vault will be a free trial with limited functionality. You can upgrade to a subscription later to get full access."
      successButtonText="Create vault"
      successStyleButton={ButtonStyle.PrimaryFilled}
      closeFn={() => {
        cancelPaymentProcess();
        trackCustomEvent('cancelVaultPayment');
      }}
      successFn={() => {
        handleConfirmOperation(true);
      }}
    />
  );

  const handleCloseProcessingPaymentModal = (
    subscriptionStatus: SubscriptionStatus,
  ) => {
    setProcessingVaultPaymentModal(false);

    if (subscriptionStatus === 'subscription_active') {
      setShowPaymentSuccesfulModal(true);
    }

    if (subscriptionStatus === 'payment_failed') {
      setShowPaymentFailedModal(true);
    }
  };

  const handleErrorProcessingPayment = () => {
    setProcessingVaultPaymentModal(false);
    notifyError();
  };

  const showConfirmPayNewVaultFullPriceModalComponent = () => (
    <ModalWarning
      loading={loading}
      title={title}
      content={confirmPaymentTextBody}
      successButtonText={payButtonText}
      successStyleButton={ButtonStyle.PrimaryFilled}
      closeFn={() => {
        cancelPaymentProcess();
        trackCustomEvent('cancelVaultPayment');
      }}
      successFn={() => {
        handleConfirmOperation(false);
      }}
    />
  );

  const addPaymentMethodModalComponent = () => (
    <AddPaymentMethodModal
      showWarningNotPayMethod
      notifyClose={() => {
        setAddPaymentMethodModal(false);
        cancelPaymentProcess();
        trackCustomEvent('cancelAddPaymentMethod');
      }}
      notifySuccess={() => {
        setAddPaymentMethodModal(false);
        startProcess();
        trackCustomEvent('confirmAddPaymentMethod');
      }}
    />
  );

  const confirmCreateVaultProcessComponent = () =>
    creatingFreeTrial
      ? showCreateFreeTrialVaultModalComponent()
      : showConfirmPayNewVaultFullPriceModalComponent();

  const handleChangeSubscriptionType = (
    subSelected: SubscriptionTypeType,
    subModificationType: SubModificationType,
    newTierSelected: SubscriptionSchemeId,
  ) => {
    subscriptionSeleted.current = subSelected;
    newTier.current = newTierSelected;

    setShowModifyVaultSubscriptionModal(false);

    if (subModificationType === SubModificationType.downgrade) {
      setShowDowngradeConfirmSubscriptionModal(true);
    } else {
      setShowUpgradeConfirmSubscriptionModal(true);
    }
  };

  const showModifyVaultSubscriptionModalComponent = () => (
    <ModifySubscriptionModal
      vault={vault!}
      onClose={() => cancelPaymentProcess()}
      notifyUpdateUserData={() => handleUupdateUserData()}
      discountedPrices={discountedPrices}
      isInsideVideoCall={isInsideVideoCall}
      onSuccess={(
        subSelected: SubscriptionTypeType,
        subModificationType: SubModificationType,
        newTierSelected: SubscriptionSchemeId,
      ) =>
        handleChangeSubscriptionType(
          subSelected,
          subModificationType,
          newTierSelected,
        )
      }
    />
  );

  const processingVaultPaymentModalComponent = () => (
    <ModalProcessingVaultPayment
      vaultResponse={vault!}
      title={title}
      disableManualClosing
      closeFn={(albResponse: VaultType) => {
        newTier.current = albResponse.subscriptionType;
        handleCloseProcessingPaymentModal(albResponse.subscriptionStatus);
      }}
      errorFn={() => handleErrorProcessingPayment()}
    />
  );

  const paymentSuccesfulContentModal = () => (
    <p className="text__body__semi__bold__large__gray800" data-cy={dataCy}>
      {successPaymentTextBody}
    </p>
  );

  const paymentFailedContentModal = () => (
    <div className={styles.failedPaymentContent}>
      <p className="text__body__semi__bold__large__gray800">
        Your card was declined.
      </p>
      <p className="text__body__semi__bold__medium__gray700">
        {failedPaymentTextBody}
      </p>
    </div>
  );

  const showPaymentSuccesfulModalComponent = () => (
    <ModalWarning
      title={title}
      disableManualClosing
      showCancelButton={false}
      content={paymentSuccesfulContentModal()}
      successButtonText={successPaymentButtonText}
      successStyleButton={ButtonStyle.PrimaryFilled}
      successFn={() => {
        setShowPaymentSuccesfulModal(false);
        successPaymentProcess(newTier.current!);
      }}
    />
  );

  const showPaymentFailedModalComponent = () => (
    <ModalWarning
      title={title}
      disableManualClosing
      content={paymentFailedContentModal()}
      successButtonText="Review payment method"
      successStyleButton={ButtonStyle.PrimaryFilled}
      successFn={() => {
        goToPage(RouteName.Account);
      }}
      closeFn={() => {
        setShowPaymentFailedModal(false);
        failedPaymentProcess();
      }}
    />
  );

  const downgradeSubContentModal = () => (
    <div className={styles.downgradeSubContentModal}>
      <p className="text__title2__gray800">Confirm subscription downgrade</p>
      <p className="text__body__regular__small__gray700">
        {`This downgrade will take effect on ${dayjs(
          vault!.nextPaymentDate,
        ).format('MM/DD/YYYY')}, when the current subscription runs out.`}
      </p>
    </div>
  );

  const showDowngradeConfirmSubscriptionModalComponent = () => (
    <ModalWarning
      loading={loading}
      title="Modify subscription"
      content={downgradeSubContentModal()}
      successButtonText="Confirm downgrade"
      successStyleButton={ButtonStyle.PrimaryFilled}
      closeFn={() => cancelPaymentProcess()}
      successFn={() =>
        handleConfirmOperation(
          false,
          SubModificationType.downgrade,
          newTier.current!,
        )
      }
    />
  );

  const showUpgradeConfirmSubscriptionModalComponent = () => (
    <UpgradeConfirmSubscriptionModal
      footerLoading={loading}
      subscriptionsScheme={subscriptionsScheme!}
      vault={vault!}
      newSubscriptionSelected={subscriptionSeleted.current!}
      discountedPrices={discountedPrices}
      closeFn={() => cancelPaymentProcess()}
      successFn={() =>
        handleConfirmOperation(
          false,
          SubModificationType.upgrade,
          newTier.current!,
        )
      }
    />
  );

  return (
    <>
      {confirmCreateVaultProcess && confirmCreateVaultProcessComponent()}
      {addPaymentMethodModal && addPaymentMethodModalComponent()}

      {processingVaultPaymentModal && processingVaultPaymentModalComponent()}
      {showPaymentSuccesfulModal && showPaymentSuccesfulModalComponent()}
      {showPaymentFailedModal && showPaymentFailedModalComponent()}

      {showModifyVaultSubscriptionModal &&
        showModifyVaultSubscriptionModalComponent()}
      {showDowngradeConfirmSubscriptionModal &&
        showDowngradeConfirmSubscriptionModalComponent()}
      {showUpgradeConfirmSubscriptionModal &&
        showUpgradeConfirmSubscriptionModalComponent()}
    </>
  );
};

export { AllPaymentProcess };
